import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { CoverImage } from './CoverImage';
import { cn } from '@/lib/utils';
import { type CoverSize } from '@/lib/coverUtils';

interface BookRowProps {
  bookId: string;
  title: string;
  author?: string;
  isbn13?: string;
  isbn10?: string;
  olid?: string;
  coverUrl?: string;
  coverCacheUrl?: string;
  href?: string;
  right?: React.ReactNode;
  size?: CoverSize;
  className?: string;
}

export function BookRow({
  bookId,
  title,
  author,
  isbn13,
  isbn10,
  olid,
  coverUrl,
  coverCacheUrl,
  href,
  right,
  size = 'md',
  className
}: BookRowProps) {
  const navigate = useNavigate();
  const bookHref = href ?? `/book/${bookId}`;
  const altText = `${title}${author ? ` by ${author}` : ''}`;

  // Get width class based on size
  const sizeWidthClass = {
    xs: 'w-12', // 48px
    sm: 'w-16', // 64px  
    md: 'w-24', // 96px
    lg: 'w-30'  // 120px
  }[size];

  return (
    <div className="border-t border-border/50 mt-2 pt-2 first:border-t-0 first:mt-0 first:pt-0">
      <div
        className={cn(
          "p-3 sm:p-4 rounded-xl bg-white/80",
          "supports-[backdrop-filter]:bg-white/60",
          className
        )}
      >
        {/* Mobile: Flex row for cover + meta, Desktop: Grid layout */}
        <div className="flex gap-3 sm:gap-4 md:grid md:grid-cols-[auto,minmax(180px,1fr),2fr] md:items-start">
          {/* LEFT — Cover */}
          <Link 
            to={bookHref} 
            aria-label={`Open ${altText}`} 
            className="block flex-shrink-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded-md"
          >
            <div className={cn(
              "relative aspect-[2/3] overflow-hidden rounded-md shadow-sm",
              sizeWidthClass
            )}>
              <CoverImage
                bookId={bookId}
                isbn13={isbn13}
                isbn10={isbn10}
                olid={olid}
                title={title}
                author={author}
                coverUrl={coverUrl}
                coverCacheUrl={coverCacheUrl}
                size={size}
                loading="lazy"
              />
            </div>
          </Link>

          {/* MIDDLE — Meta */}
          <div className="flex-1 min-w-0">
            <Link 
              to={bookHref} 
              className="group focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded-md"
            >
              <h3 className={cn(
                "font-semibold leading-snug line-clamp-2 group-hover:text-primary transition-colors",
                size === 'sm' ? 'text-sm' : 'text-sm sm:text-base md:text-lg'
              )}>
                {title}
              </h3>
            </Link>
            {author && (
              <a 
                href={`/author/${encodeURIComponent(author)}`}
                className={cn(
                  "text-muted-foreground leading-snug mt-0.5 line-clamp-1 hover:text-primary hover:underline transition-colors block",
                  size === 'sm' ? 'text-xs' : 'text-xs sm:text-sm'
                )}
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  navigate(`/author/${encodeURIComponent(author)}`);
                }}
              >
                {author}
              </a>
            )}
          </div>

          {/* RIGHT — Pluggable content (desktop only in grid) */}
          <div className="hidden md:flex min-w-0 flex-col gap-2">
            {right}
          </div>
        </div>

        {/* RIGHT content on mobile - full width below cover/meta */}
        {right && (
          <div className="md:hidden mt-3 flex flex-col gap-2">
            {right}
          </div>
        )}
      </div>
    </div>
  );
}

export default BookRow;